import React, { useEffect, useRef } from "react";

import {
  LICENSE_AGREEMENT_GROUP_SHELL_KEYS,
  LICENSE_AGREEMENT_SUP_GROUP_SHELL_KEYS,
} from "@frontend/common";

import { DealsSection } from "@/types/deals.types";
import useArticleSection from "@FillInTableContainer/hooks/useArticleSection";
import {
  GroupShellDividerPlusBar,
  TableCreatorGroupShellTitle,
} from "@FillInTableContainer/shared";
import TableDragDivider from "@FillInTableContainer/shared/components/TableDragDivider/TableDragDivider";
import { useHandleTableCreatorSubGroupShellDrag } from "@FillInTableContainer/shared/hooks/useHandleTableCreatorSubGroupShellDrag";
import {
  GroupTableKeyType,
  TableCreatorSubGroupShellList,
} from "@FillInTableContainer/shared/types/tableCreator.types";
import { isAddDeleteGroupShell } from "@FillInTableContainer/shared/utils/getOptionsFromData";
import {
  getAddedGroupShellTitle,
  getGroupShellTitle,
} from "@FillInTableContainer/shared/utils/getTextFromData";
import IconXMark from "@images/fillInTable/tableCreator/icon-x-mark-white.svg";
import { newArrayToCountValue } from "@utils/newArrayToCountValue";

import DealTermsLicenseAgreementSubGroupShell from "./components/DealTermsLicenseAgreementSubGroupShell/DealTermsLicenseAgreementSubGroupShell";
import * as S from "./DealTermsLicenseAgreementGroupShell.style";
import useCheckIsDisplaySubGroupShellDragDivider from "./hooks/useCheckIsDisplaySubGroupShellDragDivider";
import { useGetNumberOfSupGroupShell } from "./hooks/useGetNumberOfSubGroupShell";
import { useHandleLicenseAgreementTableCreatorGroupShell } from "./hooks/useHandleLicenseAgreementTableCreatorGroupShell";

interface Props {
  groupTableKey: DealsSection;
  groupTableIndex: number;
  groupShellSectionKey: string | null;
  groupShellSectionIndex: number | null;
  groupShellKey: string | null;
  subGroupShellList: TableCreatorSubGroupShellList;
  tableKey: string | null;
  tableIndex: number;
  groupShellIndex: number;
  groupShellCount: number;
  handleDragStart: (
    event: React.DragEvent<HTMLDivElement>,
    index: number,
  ) => void;
  handleDragEnd: (groupTableKey: GroupTableKeyType) => void;
  handleDragOn: () => void;
  isDraggableGroupShell: boolean;
  isFocusGroupShellContainer: boolean;
}

const isNotFocusGroupShell: string[] = [
  LICENSE_AGREEMENT_GROUP_SHELL_KEYS.financialTerms,
];
const dragSubGroupShellList: string[] = [
  LICENSE_AGREEMENT_SUP_GROUP_SHELL_KEYS.milestone,
  LICENSE_AGREEMENT_SUP_GROUP_SHELL_KEYS.otherPaymentAndDetail,
];
const ARTICLE_SECTION = "Deals";

const DealTermsLicenseAgreementGroupShell = ({
  groupShellCount,
  groupShellIndex,
  groupShellKey,
  groupTableIndex,
  groupTableKey,
  groupShellSectionKey,
  groupShellSectionIndex,
  subGroupShellList,
  tableIndex,
  tableKey,
  handleDragEnd,
  handleDragOn,
  handleDragStart,
  isDraggableGroupShell,
  isFocusGroupShellContainer,
}: Props) => {
  const positionPropsInfo = {
    groupTableKey,
    groupTableIndex,
    groupShellKey,
    groupShellIndex,
    groupShellSectionKey,
    groupShellSectionIndex,
    tableKey,
    tableIndex,
  };
  const { subGroupShellCount } = useGetNumberOfSupGroupShell();
  const articleSection = useArticleSection();
  const selectButtonRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);

  const {
    focusStatus,
    currentInfoStatus,
    isFocused,
    isClearGroupShell,
    isDeleteGroupShell,
    isAlertGroupShell,
    isEnableEdit,
    isLastGroupShell,
    isFocusedAddButton,
    handleFocusGroupShell,
    clickAddGroupShellButton,
    handleClickToDeleteGroupShell,
    handleDefaultFocus,
    changeFocusStatus,
    changePositionInfo,
    changePositionInfoToDefault,
  } = useHandleLicenseAgreementTableCreatorGroupShell({
    positionPropsInfo,
    groupShellCount,
  });

  const {
    handleSubGroupShellDragEnd,
    handleSubGroupShellDragOn,
    handleSubGroupShellDragOver,
    handleSubGroupShellDragStart,
    subGroupShellDraggedIndexInfo,
    isDraggingSubGroupShell,
  } = useHandleTableCreatorSubGroupShellDrag(ARTICLE_SECTION, tableIndex);

  const { isLastSubGroupShell, handleCheckDragDividerDisplay } =
    useCheckIsDisplaySubGroupShellDragDivider({
      tableIndex,
      groupShellIndex,
      isDraggingSubGroupShell,
    });

  useEffect(() => {
    const handleCheckClickOutsideShell = (e: MouseEvent) => {
      if (isFocused) {
        const target = e.target as HTMLElement;
        const clickSelectButton = selectButtonRef.current?.contains(target);
        const clickGroupShellTitle = titleRef.current?.contains(target);

        if (clickSelectButton || clickGroupShellTitle) return;
        if (target.dataset.button) return;

        handleDefaultFocus();
      }
    };

    window.addEventListener("click", handleCheckClickOutsideShell);

    return () =>
      window.removeEventListener("click", handleCheckClickOutsideShell);
  }, [handleDefaultFocus, isDeleteGroupShell, isFocused]);

  const isPossibleSelected = groupShellKey
    ? !isNotFocusGroupShell.includes(groupShellKey)
    : true;

  const checkIsDisplayDragDividerSubGroupShell = (
    subGroupShellKey: string | null,
  ) => {
    if (!subGroupShellKey) return false;

    return dragSubGroupShellList.includes(subGroupShellKey || "");
  };

  return (
    <>
      {isAddDeleteGroupShell(articleSection, groupShellKey) && (
        <GroupShellDividerPlusBar
          onClick={(event) => {
            clickAddGroupShellButton(event, {
              tableIndexParameter: tableIndex,
              groupShellIndexParameter: groupShellIndex,
              groupShellKeyParameter: groupShellKey,
            });
          }}
          isLastPosition={false}
          buttonText="Add Rights Scope"
          isVisibility={isFocusedAddButton}
        />
      )}
      <S.GroupShellOutsideWrapper>
        {(isClearGroupShell || isDeleteGroupShell) && isEnableEdit && (
          <S.FixedDeleteButtonWrapper
            isShellTitle={!!getGroupShellTitle(articleSection, groupShellKey)}
          >
            <S.DeleteTableButtonWrapper>
              <S.DeleteTableButton
                data-button="delete-button"
                onClick={handleClickToDeleteGroupShell}
              >
                <img src={IconXMark} alt="button for clear Row" />
                {focusStatus.type === "clear" ? `Clear` : null}
                {focusStatus.type === "delete" ? `Delete` : null}
              </S.DeleteTableButton>
            </S.DeleteTableButtonWrapper>
          </S.FixedDeleteButtonWrapper>
        )}

        <S.GroupShellWrapper
          isFocused={isFocused}
          isClearGroupShell={isClearGroupShell}
          isDeleteGroupShell={isDeleteGroupShell}
          isAlertGroupShell={isAlertGroupShell}
          isDragging={isFocused && focusStatus.type === "dragging"}
        >
          <S.TitleWrapper ref={titleRef}>
            {getAddedGroupShellTitle(articleSection, groupShellKey) && (
              <TableCreatorGroupShellTitle
                groupShellKey={groupShellKey}
                groupShellCount={groupShellCount}
                groupShellIndex={groupShellIndex}
                isAlertGroupShell={isAlertGroupShell}
                onClickHandler={handleFocusGroupShell}
                groupTableKey={groupTableKey}
                handleDragStart={handleDragStart}
                handleDragEnd={handleDragEnd}
                handleDragOn={handleDragOn}
                isDragging={isFocused && focusStatus.type === "dragging"}
                isDraggableGroupShell={isDraggableGroupShell}
                changeFocusStatusForDragging={() => {
                  changePositionInfo();
                  changeFocusStatus({
                    type: "dragging",
                    container: "groupShell",
                  });
                }}
                changeFocusStatusForDragEnd={() => {
                  changePositionInfoToDefault();
                  changeFocusStatus({
                    type: "default",
                    container: "noFocus",
                  });
                }}
              />
            )}
          </S.TitleWrapper>
          <S.SelectWrapper
            $isAddedGroupShell={isAddDeleteGroupShell(
              articleSection,
              groupShellKey,
            )}
          >
            <S.GroupShellSelectButtonDividerWrapper
              ref={selectButtonRef}
              onClick={handleFocusGroupShell}
              isPossibleSelected={isPossibleSelected}
              isFinancialTerms={
                groupShellKey ===
                LICENSE_AGREEMENT_GROUP_SHELL_KEYS.financialTerms
              }
            >
              <S.GroupShellSelectButtonDivider className="select-button divider" />
              <S.GroupShellSelectButton className="select-button button">
                SELECT
              </S.GroupShellSelectButton>
            </S.GroupShellSelectButtonDividerWrapper>
            <S.GroupShell>
              {subGroupShellList.map(
                ({ subGroupShellKey, shellList }, shellListIndex) => (
                  <React.Fragment key={`shellList-${String(shellListIndex)}`}>
                    {subGroupShellKey ===
                      LICENSE_AGREEMENT_SUP_GROUP_SHELL_KEYS.otherPaymentAndDetail && (
                      <S.SubGroupShellDivider />
                    )}
                    {newArrayToCountValue(
                      subGroupShellCount(
                        subGroupShellKey,
                        tableIndex,
                        groupShellIndex,
                      ),
                    ).map((_, subGroupShellIndex) => (
                      <S.DraggableSubGroupShellWrapper
                        isTargetDraggingTable={
                          subGroupShellKey ===
                            currentInfoStatus.subGroupShellKey &&
                          isDraggingSubGroupShell &&
                          subGroupShellDraggedIndexInfo[0] !==
                            subGroupShellIndex &&
                          checkIsDisplayDragDividerSubGroupShell(
                            subGroupShellKey,
                          )
                        }
                        onDragOver={(e) =>
                          handleSubGroupShellDragOver(e, subGroupShellIndex)
                        }
                      >
                        {checkIsDisplayDragDividerSubGroupShell(
                          subGroupShellKey,
                        ) && (
                          <TableDragDivider
                            isDraggingEndPosition={
                              subGroupShellKey ===
                                currentInfoStatus.subGroupShellKey &&
                              handleCheckDragDividerDisplay({
                                moveDragIndex: subGroupShellDraggedIndexInfo[1],
                                startDragIndex:
                                  subGroupShellDraggedIndexInfo[0],
                                subGroupShellIndex,
                                subGroupShellKey,
                              })
                            }
                          />
                        )}
                        <DealTermsLicenseAgreementSubGroupShell
                          key={`shell-${String(groupTableIndex)}-${String(
                            tableIndex,
                          )}-${String(groupShellIndex)}-${String(
                            subGroupShellIndex,
                          )}`}
                          groupTableKey={groupTableKey}
                          groupTableIndex={groupTableIndex}
                          groupShellKey={groupShellKey}
                          groupShellIndex={groupShellIndex}
                          subGroupShellKey={subGroupShellKey}
                          shellList={shellList}
                          subGroupShellIndex={subGroupShellIndex}
                          tableKey={tableKey}
                          tableIndex={tableIndex}
                          subGroupShellCount={subGroupShellCount(
                            subGroupShellKey,
                            tableIndex,
                            groupShellIndex,
                          )}
                          isShellOverOne={
                            subGroupShellCount(
                              subGroupShellKey,
                              tableIndex,
                              groupShellIndex,
                            ) > 1
                          }
                          isFocusedGroupShell={isFocused}
                          isClearGroupShell={isClearGroupShell}
                          isFocusGroupShellContainer={
                            isFocusGroupShellContainer
                          }
                          isDeleteGroupShell={isDeleteGroupShell}
                          isAlertGroupShell={isAlertGroupShell}
                          handleDragEnd={handleSubGroupShellDragEnd}
                          handleDragOn={handleSubGroupShellDragOn}
                          handleDragStart={handleSubGroupShellDragStart}
                        />
                        {checkIsDisplayDragDividerSubGroupShell(
                          subGroupShellKey,
                        ) &&
                          isLastSubGroupShell(
                            subGroupShellIndex,
                            subGroupShellKey,
                          ) && (
                            <TableDragDivider
                              isDraggingEndPosition={
                                subGroupShellKey ===
                                  currentInfoStatus.subGroupShellKey &&
                                handleCheckDragDividerDisplay({
                                  moveDragIndex:
                                    subGroupShellDraggedIndexInfo[1],
                                  startDragIndex:
                                    subGroupShellDraggedIndexInfo[0],
                                  subGroupShellIndex,
                                  subGroupShellKey,
                                  lastIndex: newArrayToCountValue(
                                    subGroupShellCount(
                                      subGroupShellKey,
                                      tableIndex,
                                      groupShellIndex,
                                    ),
                                  ).length,
                                })
                              }
                            />
                          )}
                      </S.DraggableSubGroupShellWrapper>
                    ))}
                  </React.Fragment>
                ),
              )}
            </S.GroupShell>
          </S.SelectWrapper>
        </S.GroupShellWrapper>
      </S.GroupShellOutsideWrapper>
      {isAddDeleteGroupShell(articleSection, groupShellKey) &&
        isLastGroupShell && (
          <GroupShellDividerPlusBar
            onClick={(event) => {
              clickAddGroupShellButton(event, {
                tableIndexParameter: tableIndex,
                groupShellIndexParameter: groupShellIndex + 1,
                groupShellKeyParameter: groupShellKey,
              });
            }}
            buttonText="Add Rights Scope"
            isLastPosition
            isVisibility={isFocusedAddButton}
          />
        )}
    </>
  );
};

export default DealTermsLicenseAgreementGroupShell;
